import React from "react";

// Stylised cinematic SVG turbine + glass overlay + blueprint wireframe.
// The five layers can be cross-faded via parent opacity for the "X-Ray
// engineering mode" (Polished → Glass → Internals → Wireframe → Assembled).

const Polished = ({ className = "" }) => (
    <svg
        viewBox="0 0 600 600"
        className={className}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
    >
        <defs>
            <radialGradient id="metalA" cx="50%" cy="35%" r="65%">
                <stop offset="0%" stopColor="#ffffff" />
                <stop offset="55%" stopColor="#e9eaee" />
                <stop offset="100%" stopColor="#9aa0aa" />
            </radialGradient>
            <linearGradient id="metalB" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#d8dade" />
                <stop offset="50%" stopColor="#ffffff" />
                <stop offset="100%" stopColor="#a8acb4" />
            </linearGradient>
            <linearGradient id="ringG" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00e5ff" />
                <stop offset="100%" stopColor="#0a84ff" />
            </linearGradient>
            <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="6" />
                <feOffset dy="4" />
                <feComponentTransfer>
                    <feFuncA type="linear" slope="0.35" />
                </feComponentTransfer>
                <feMerge>
                    <feMergeNode />
                    <feMergeNode in="SourceGraphic" />
                </feMerge>
            </filter>
        </defs>

        {/* Long shaft */}
        <g filter="url(#softShadow)">
            <rect
                x="120"
                y="270"
                width="320"
                height="62"
                rx="14"
                fill="url(#metalB)"
            />
            {/* Knurled grip rings */}
            {[180, 200, 220, 240, 260, 280, 300].map((x) => (
                <rect
                    key={x}
                    x={x}
                    y="276"
                    width="2"
                    height="50"
                    fill="#9aa0aa"
                    opacity="0.55"
                />
            ))}
            {/* Cyan precision ring */}
            <rect x="328" y="270" width="10" height="62" fill="url(#ringG)" />
            <rect x="346" y="270" width="3" height="62" fill="#0a84ff" opacity="0.35" />

            {/* Neck */}
            <path
                d="M440 286 L478 256 L500 264 L478 296 L500 312 L478 328 L500 346 L478 356 L440 326 Z"
                fill="url(#metalA)"
                stroke="#9aa0aa"
                strokeWidth="0.6"
            />

            {/* Head (tete) */}
            <circle cx="498" cy="306" r="46" fill="url(#metalA)" stroke="#9aa0aa" strokeWidth="0.8" />
            <circle cx="498" cy="306" r="34" fill="#1c1c1e" />
            <circle cx="498" cy="306" r="30" fill="#0b0b0d" />

            {/* Rotor blades */}
            <g className="dp-spin-slow" style={{ transformOrigin: "498px 306px" }}>
                {[...Array(8)].map((_, i) => {
                    const angle = (i * 360) / 8;
                    return (
                        <rect
                            key={i}
                            x="496"
                            y="282"
                            width="4"
                            height="22"
                            rx="1.2"
                            fill="#d1d1d6"
                            transform={`rotate(${angle} 498 306)`}
                        />
                    );
                })}
                <circle cx="498" cy="306" r="6" fill="#00e5ff" />
            </g>

            {/* Bur / fraise */}
            <rect x="497" y="350" width="2" height="60" fill="#c7cad0" />
            <rect x="495" y="408" width="6" height="10" fill="#8e8e93" />

            {/* End cap */}
            <circle cx="130" cy="301" r="22" fill="url(#metalA)" stroke="#9aa0aa" strokeWidth="0.6" />
            <circle cx="130" cy="301" r="9" fill="#1c1c1e" />
        </g>
    </svg>
);

const GlassLayer = ({ className = "" }) => (
    <svg
        viewBox="0 0 600 600"
        className={className}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
    >
        <defs>
            <linearGradient id="glassG" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0.65" />
                <stop offset="60%" stopColor="#bfeaff" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#0a84ff" stopOpacity="0.12" />
            </linearGradient>
        </defs>
        <rect x="120" y="270" width="320" height="62" rx="14" fill="url(#glassG)" />
        <circle cx="498" cy="306" r="46" fill="url(#glassG)" />
        <path
            d="M440 286 L478 256 L500 264 L478 296 L500 312 L478 328 L500 346 L478 356 L440 326 Z"
            fill="url(#glassG)"
        />
        <rect x="130" y="285" width="280" height="6" rx="3" fill="#ffffff" opacity="0.7" />
        <rect x="130" y="312" width="280" height="3" rx="1.5" fill="#ffffff" opacity="0.35" />
    </svg>
);

const Internals = ({ className = "" }) => (
    <svg
        viewBox="0 0 600 600"
        className={className}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
    >
        {/* Air channels */}
        <g stroke="#00e5ff" strokeWidth="1.2" fill="none" opacity="0.85">
            <path d="M140 295 L420 295" strokeDasharray="6 4" />
            <path d="M140 308 L420 308" strokeDasharray="2 4" />
            <path d="M420 295 C 450 295 460 270 478 270" />
            <path d="M420 308 C 450 308 470 340 478 340" />
        </g>
        {/* Bearings */}
        <g stroke="#0a84ff" strokeWidth="1" fill="none">
            <circle cx="498" cy="306" r="40" />
            <circle cx="498" cy="306" r="34" />
            <circle cx="498" cy="306" r="14" />
            {[...Array(12)].map((_, i) => {
                const a = (i * Math.PI * 2) / 12;
                const x = 498 + Math.cos(a) * 24;
                const y = 306 + Math.sin(a) * 24;
                return <circle key={i} cx={x} cy={y} r="2.5" fill="#00e5ff" />;
            })}
        </g>
        {/* Spray nozzles */}
        <g fill="#00e5ff">
            <circle cx="484" cy="278" r="2" />
            <circle cx="512" cy="278" r="2" />
            <circle cx="484" cy="334" r="2" />
            <circle cx="512" cy="334" r="2" />
        </g>
    </svg>
);

const Wireframe = ({ className = "" }) => (
    <svg
        viewBox="0 0 600 600"
        className={className}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
    >
        <g stroke="#0a84ff" strokeWidth="0.8" fill="none" opacity="0.85">
            <rect x="120" y="270" width="320" height="62" rx="14" />
            <line x1="120" y1="301" x2="440" y2="301" />
            <circle cx="498" cy="306" r="46" />
            <circle cx="498" cy="306" r="30" />
            <circle cx="498" cy="306" r="6" />
            <path d="M440 286 L478 256 L500 264 L478 296 L500 312 L478 328 L500 346 L478 356 L440 326 Z" />
            <line x1="497" y1="350" x2="497" y2="418" />
            {[140, 180, 220, 260, 300, 340, 380, 420].map((x) => (
                <line key={x} x1={x} y1="262" x2={x} y2="340" strokeDasharray="2 4" opacity="0.55" />
            ))}
            {/* Dimension lines */}
            <line x1="120" y1="240" x2="440" y2="240" strokeDasharray="3 3" />
            <text x="270" y="232" fill="#0a84ff" fontSize="11" fontFamily="JetBrains Mono">
                L 142.0 mm
            </text>
            <line x1="498" y1="356" x2="560" y2="356" strokeDasharray="3 3" />
            <text x="510" y="376" fill="#0a84ff" fontSize="11" fontFamily="JetBrains Mono">
                Ø 12.6
            </text>
        </g>
    </svg>
);

const TurbineHero = ({ stage = 0 }) => {
    // Five stages map to opacity profiles for cross-fade.
    const op = (s) => {
        const a = Math.max(0, 1 - Math.abs(stage - s));
        return a;
    };
    return (
        <div className="relative w-full h-full select-none">
            {/* Soft glow halo */}
            <div
                className="absolute inset-0 dp-pulse"
                style={{
                    background:
                        "radial-gradient(45% 35% at 60% 50%, rgba(0,229,255,0.22), transparent 70%)",
                    filter: "blur(8px)",
                }}
            />
            <div
                className="absolute inset-0 transition-opacity duration-700"
                style={{ opacity: Math.min(1, op(0) + op(4) * 0.95) }}
            >
                <Polished className="w-full h-full" />
            </div>
            <div
                className="absolute inset-0 transition-opacity duration-700"
                style={{ opacity: Math.max(op(1), op(4) * 0.25) }}
            >
                <GlassLayer className="w-full h-full" />
            </div>
            <div
                className="absolute inset-0 transition-opacity duration-700"
                style={{ opacity: op(2) }}
            >
                <Internals className="w-full h-full" />
            </div>
            <div
                className="absolute inset-0 transition-opacity duration-700"
                style={{ opacity: Math.max(op(3), op(4) * 0.2) }}
            >
                <Wireframe className="w-full h-full" />
            </div>
        </div>
    );
};

// Inline-style polyfill since the props pass `style` correctly to SVGs.
[Polished, GlassLayer, Internals, Wireframe].forEach((C) => {
    C.defaultProps = { ...C.defaultProps };
});

export default TurbineHero;
